'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { type JSX, useState } from 'react';

import { Search } from '@videoblocks/react-icons';

import TopNavSearchBar from './TopNavSearchBar';
import DesktopNavActions from './desktop/DesktopNavActions';
import DesktopNavItem from './desktop/DesktopNavItem';
import MobileNav from './mobile/MobileNav';
import TabletNavActions from './tablet/TabletNavActions';
import type { DesktopNavConfig, MobileNavConfig, TabletNavConfig } from './types';

interface TopNavigationBarProps {
    desktop: DesktopNavConfig;
    tablet: TabletNavConfig;
    mobile: MobileNavConfig;
    darkHeader?: boolean;
    showSearchByDefault?: boolean;
    showMobileSearchBar?: boolean;
}

export default function TopNavigationBar({
    desktop,
    tablet,
    mobile,
    darkHeader = false,
    showSearchByDefault = false,
    showMobileSearchBar = true,
}: TopNavigationBarProps): JSX.Element {
    const [isSearchExpanded, setIsSearchExpanded] = useState(showSearchByDefault);

    return (
        <nav
            aria-label="Main navigation"
            className={classNames(
                'fixed top-0 z-50 w-full flex flex-col gap-4 px-4 py-4 topnav-xl:px-6 topnav-xl:py-4 border-b',
                {
                    'topnav-xl:pb-0': isSearchExpanded,
                    'border-gray-700 bg-gray-900': darkHeader,
                    'border-gray-200 bg-white': !darkHeader,
                },
            )}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-5">
                    <a href="/" className="shrink-0">
                        <Image
                            src={darkHeader ? '/assets/images/sb-logo-white.svg' : '/assets/images/sb-logo.svg'}
                            alt="Storyblocks"
                            width={151}
                            height={32}
                            priority
                        />
                    </a>

                    {/* Tablet nav items — visible lg to topnav-xl */}
                    <div className="hidden items-center lg:flex topnav-xl:hidden">
                        {tablet.navItems.map((item) => (
                            <DesktopNavItem key={item.id} config={item} darkHeader={darkHeader} />
                        ))}
                    </div>

                    {/* Desktop nav items + search icon — visible topnav-xl and up */}
                    <div className="hidden items-center topnav-xl:flex">
                        {desktop.navItems.map((item) => (
                            <DesktopNavItem key={item.id} config={item} darkHeader={darkHeader} />
                        ))}
                        {!isSearchExpanded && (
                            <button
                                type="button"
                                onClick={() => setIsSearchExpanded(true)}
                                aria-label="Open search"
                                className={classNames('ml-2 p-2', {
                                    'text-white hover:text-gray-300': darkHeader,
                                    'text-gray-600 hover:text-gray-900': !darkHeader,
                                })}
                            >
                                <Search className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Right side — Desktop actions + Tablet actions + Mobile hamburger */}
                <div className="flex items-center gap-4">
                    <DesktopNavActions items={desktop.actions} darkHeader={darkHeader} />
                    <TabletNavActions
                        actions={tablet.actions}
                        hamburgerNavItems={tablet.hamburgerNavItems}
                        overlayActions={tablet.overlayActions}
                        darkHeader={darkHeader}
                    />
                    <MobileNav config={mobile} darkHeader={darkHeader} />
                </div>
            </div>
            {/* Desktop: expanded search bar */}
            {isSearchExpanded && (
                <div className="hidden topnav-xl:flex justify-center pb-2 px-6">
                    <TopNavSearchBar onCollapse={() => setIsSearchExpanded(false)} showCloseButton />
                </div>
            )}
            {/* Tablet + Mobile: search bar (suppressible for lite layouts) */}
            {showMobileSearchBar && (
                <div className="topnav-xl:hidden">
                    <TopNavSearchBar />
                </div>
            )}
        </nav>
    );
}
